import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomers } from '../store/slices/customersSlice';
import { apiService } from '../services/api';
import './ImageBroadcastModal.css';

/**
 * ImageBroadcastModal
 *
 * Sends a single selected image to chosen recipients.
 * Logic:
 *  - If IMAGE_HAWK templateSettings record has status APPROVED:
 *      • Selected image → POST /messages/send/image-template
 *  - Otherwise:
 *      • Selected image → POST /messages/send/image-url
 */
const ImageBroadcastModal = ({ isOpen, onClose, selectedImages = [], serverBaseUrl = '' }) => {
  const dispatch = useDispatch();
  const { customers } = useSelector(state => state.customers);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [caption, setCaption] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [done, setDone] = useState(false);
  const [imageHawkTemplate, setImageHawkTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);

  const totalSteps = 3;
  const stepTitles = ['Review Images', 'Select Recipients', 'Send'];

  // Load customers and IMAGE_HAWK template status on open
  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchCustomers({ limit: 500 }));
    setTemplateLoading(true);
    apiService.templateSettings.getPredefinedStatus()
      .then(res => {
        const templates = res.data?.data || [];
        const hawkTpl = templates.find(t => t.type === 'IMAGE_HAWK' || t.id === 'image-hawk');
        setImageHawkTemplate(hawkTpl || null);
      })
      .catch(() => setImageHawkTemplate(null))
      .finally(() => setTemplateLoading(false));
    // Reset state
    setCurrentStep(1);
    setSelectedRecipients([]);
    setRecipientSearch('');
    setCaption('');
    setSending(false);
    setProgress({ done: 0, total: 0 });
    setErrors([]);
    setDone(false);
  }, [isOpen, dispatch]);

  const isImageHawkApproved = imageHawkTemplate?.status === 'APPROVED';

  const effectiveRecipients = useMemo(() =>
    (customers || []).map(c => ({
      id: c._id || c.id,
      name: c.name,
      phone: c.phoneNumber || c.phone
    })).filter(r => r.phone),
    [customers]
  );

  const filteredRecipients = useMemo(() =>
    effectiveRecipients.filter(r =>
      r.name.toLowerCase().includes(recipientSearch.toLowerCase()) ||
      (r.phone || '').includes(recipientSearch)
    ),
    [effectiveRecipients, recipientSearch]
  );

  const toggleRecipient = (id) => {
    setSelectedRecipients(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const handleSelectAllRecipients = () => {
    if (selectedRecipients.length === effectiveRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(effectiveRecipients.map(r => r.id));
    }
  };

  const validateStep = (step) => {
    if (step === 1 && selectedImages.length === 0) return false;
    if (step === 2 && selectedRecipients.length === 0) return false;
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep(s => Math.min(s + 1, totalSteps));
  };

  const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleSend = async () => {
    setSending(true);
    setErrors([]);
    const recipients = effectiveRecipients.filter(r => selectedRecipients.includes(r.id));
    const selectedImage = selectedImages[0];
    const imageUrl = selectedImage?.url || selectedImage?.mediaUrl || '';
    const totalOps = recipients.length;
    setProgress({ done: 0, total: totalOps });

    const sendErrors = [];
    let doneCount = 0;

    for (const recipient of recipients) {
      for (let imgIdx = 0; imgIdx < selectedImages.length; imgIdx++) {
        const img = selectedImages[imgIdx];
        try {
          if (imgIdx === 0 && isImageHawkApproved) {
            // Subsequent images (or all if not approved) via direct image URL
            await apiService.messages.sendImageSample({
              toPhoneNumber: recipient.phone,
              imageUrl: img.url,
              caption: imgIdx === 0 ? (caption || undefined) : undefined
            });
          } else {
            // Subsequent images (or all if not approved) via direct image URL
            await apiService.messages.sendImageSample({
              toPhoneNumber: recipient.phone,
              imageUrl: img.url,
              caption: imgIdx === 0 ? (caption || undefined) : undefined
            });
          }
        } catch (err) {
          const errMsg = err.response?.data?.error || err.message || 'Unknown error';
          sendErrors.push(`${recipient.name} (${recipient.phone}) — Image ${imgIdx + 1}: ${errMsg}`);
        }
        doneCount++;
        setProgress({ done: doneCount, total: totalOps });
      }
    }

    setSending(false);
    setErrors(sendErrors);
    setDone(true);
  };

  if (!isOpen) return null;

  return (
    <div className="ib-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ib-modal">
        {/* Header */}
        <div className="ib-header">
          <div className="ib-header-content">
            <div className="ib-header-badge">📸 Image Broadcast</div>
            <div>
              <h2>Send image to recipients</h2>
              <p>Review the image, choose your audience, and send with confidence.</p>
            </div>
          </div>
          <button className="ib-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Step progress */}
        <div className="ib-steps">
          {stepTitles.map((title, i) => (
            <div key={i} className={`ib-step${i + 1 === currentStep ? ' active' : ''}${i + 1 < currentStep ? ' completed' : ''}`}>
              <div className="ib-step-dot">{i + 1 < currentStep ? '✓' : i + 1}</div>
              <span>{title}</span>
            </div>
          ))}
        </div>

        <div className="ib-body">
          {/* ── Step 1: Image Review ── */}
          {currentStep === 1 && (
            <div className="ib-step-content">
              <div className="ib-template-banner">
                {templateLoading ? (
                  <span className="ib-banner-loading">Checking IMAGE_HAWK template status…</span>
                ) : isImageHawkApproved ? (
                  <span className="ib-banner-approved">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    IMAGE_HAWK template is <strong>APPROVED</strong> — first selected image will be sent via template
                  </span>
                ) : (
                  <span className="ib-banner-warn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                    IMAGE_HAWK template is <strong>not approved</strong> — all images sent as direct messages
                  </span>
                )}
              </div>

              <div className="ib-images-grid">
                {selectedImages.slice(0, 1).map((img, idx) => (
                  <div key={img.id} className="ib-image-card ib-image-card--featured">
                    <div className="ib-image-order">1</div>
                    {isImageHawkApproved && (
                      <div className="ib-image-badge">Template</div>
                    )}
                    <img src={serverBaseUrl + img.url} alt={img.filename || img.id} />
                    <div className="ib-image-meta">
                      <p className="ib-image-name">{img.filename || img.id}</p>
                      <span className="ib-image-size">Ready to send</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ib-caption-group">
                <label>Caption (optional, applied to direct image messages)</label>
                <input
                  type="text"
                  placeholder="Enter a caption…"
                  value={caption}
                  onChange={e => setCaption(e.target.value)}
                  maxLength={1024}
                />
              </div>
            </div>
          )}

          {/* ── Step 2: Recipients ── */}
          {currentStep === 2 && (
            <div className="ib-step-content">
              <div className="ib-recipients-header">
                <input
                  type="text"
                  className="ib-recipient-search"
                  placeholder="Search by name or phone…"
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                />
                <button className="ib-select-all-btn" onClick={handleSelectAllRecipients}>
                  {selectedRecipients.length === effectiveRecipients.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {selectedRecipients.length > 0 && (
                <div className="ib-selected-count">{selectedRecipients.length} recipient{selectedRecipients.length > 1 ? 's' : ''} selected</div>
              )}
              {filteredRecipients.length === 0 ? (
                <div className="ib-empty">No customers found{recipientSearch ? ' matching your search' : ''}.</div>
              ) : (
                <div className="ib-recipient-list">
                  {filteredRecipients.map(r => (
                    <label key={r.id} className={`ib-recipient-row${selectedRecipients.includes(r.id) ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(r.id)}
                        onChange={() => toggleRecipient(r.id)}
                      />
                      <div className="ib-recipient-avatar">{(r.name || '?').charAt(0).toUpperCase()}</div>
                      <div className="ib-recipient-info">
                        <span className="ib-recipient-name">{r.name}</span>
                        <span className="ib-recipient-phone">{r.phone}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Send ── */}
          {currentStep === 3 && (
            <div className="ib-step-content ib-send-step">
              {!done && !sending && (
                <div className="ib-send-summary">
                  <div className="ib-summary-row">
                    <span>Images</span>
                    <strong>{selectedImages.length}</strong>
                  </div>
                  <div className="ib-summary-row">
                    <span>Recipients</span>
                    <strong>{selectedRecipients.length}</strong>
                  </div>
                  <div className="ib-summary-row">
                    <span>Total messages</span>
                    <strong>{selectedImages.length * selectedRecipients.length}</strong>
                  </div>
                  <div className="ib-summary-row">
                    <span>First image delivery</span>
                    <strong>{isImageHawkApproved ? 'IMAGE_HAWK template' : 'Direct image'}</strong>
                  </div>
                </div>
              )}

              {sending && (
                <div className="ib-progress">
                  <div className="ib-progress-bar">
                    <div
                      className="ib-progress-fill"
                      style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <p>Sending {progress.done} / {progress.total} messages…</p>
                </div>
              )}

              {done && (
                <div className="ib-result">
                  {errors.length === 0 ? (
                    <div className="ib-result-success">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      <p>All {progress.total} messages sent successfully!</p>
                    </div>
                  ) : (
                    <div className="ib-result-partial">
                      <p><strong>{progress.total - errors.length}</strong> sent, <strong>{errors.length}</strong> failed</p>
                      <ul className="ib-error-list">
                        {errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ib-footer">
          {!done ? (
            <>
              {currentStep > 1 && (
                <button className="ib-btn ib-btn--secondary" onClick={handleBack} disabled={sending}>Back</button>
              )}
              <div style={{ flex: 1 }}></div>
              {currentStep < totalSteps && (
                <button
                  className="ib-btn ib-btn--primary"
                  onClick={handleNext}
                  disabled={!validateStep(currentStep)}
                >
                  Next
                </button>
              )}
              {currentStep === totalSteps && (
                <button
                  className="ib-btn ib-btn--send"
                  onClick={handleSend}
                  disabled={sending || selectedRecipients.length === 0}
                >
                  {sending ? 'Sending…' : 'Send Now'}
                </button>
              )}
            </>
          ) : (
            <>
              <div style={{ flex: 1 }}></div>
              <button className="ib-btn ib-btn--primary" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageBroadcastModal;
